import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useApp } from "../../context/AppContext";
import { assistantApi, jobsApi } from "../../services/api";
import EmptyState from "../../components/EmptyState";
import AccessNotice, { UsageLeft } from "../../components/AccessNotice";
import { copyText, downloadResponse } from "../../utils/download";
import { applyEdits } from "../../utils/resume";

function ScoreBar({ label, value, color }) {
  return (
    <div className="mb-2">
      <div className="d-flex justify-content-between small mb-1">
        <span className="text-muted">{label}</span>
        <span className="fw-semibold">{value}%</span>
      </div>
      <div className="progress" style={{ height: 8 }}>
        <div className="progress-bar" style={{ width: `${value}%`, background: color }} />
      </div>
    </div>
  );
}

function SuggestionCard({ suggestion: s, notFound, onChange }) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(s.after);

  const startEdit = () => {
    setText(s.after);
    setEditing(true);
  };
  const saveEdit = () => {
    const after = text.trim();
    if (after && after !== s.after) {
      onChange({ after, accepted: true, suggested: s.suggested ?? s.after });
    }
    setEditing(false);
  };
  const revert = () => onChange({ after: s.suggested, suggested: undefined });

  return (
    <div className={`suggestion ${s.accepted ? "" : "rejected"}`}>
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-2">
        <span className="small fw-semibold">
          {s.section}
          {s.suggested !== undefined && <span className="badge text-bg-light ms-2 fw-normal">Edited by you</span>}
        </span>
        {!editing && (
          <div className="d-flex align-items-center gap-2">
            <button type="button" className="btn btn-link btn-sm p-0 text-decoration-none" onClick={startEdit}>
              <i className="bi bi-pencil me-1" />
              Edit
            </button>
            <div className="form-check form-switch mb-0">
              <input
                className="form-check-input"
                type="checkbox"
                id={`sg-${s.id}`}
                checked={s.accepted}
                onChange={() => onChange({ accepted: !s.accepted })}
              />
              <label className="form-check-label small" htmlFor={`sg-${s.id}`}>
                {s.accepted ? "Keep" : "Skip"}
              </label>
            </div>
          </div>
        )}
      </div>
      <div className="diff-before">{s.before}</div>
      {editing ? (
        <>
          <textarea
            className="form-control form-control-sm"
            rows={3}
            value={text}
            onChange={(e) => setText(e.target.value)}
            aria-label="Edit this line"
            autoFocus
          />
          <div className="d-flex justify-content-between align-items-center mt-2">
            <span className={`small ${text.length > s.before.length * 1.15 ? "text-warning-emphasis" : "text-muted"}`}>
              {text.length} / {s.before.length} characters in the original
            </span>
            <div className="d-flex gap-2">
              <button type="button" className="btn btn-sm btn-light" onClick={() => setEditing(false)}>
                Cancel
              </button>
              <button type="button" className="btn btn-sm btn-primary" onClick={saveEdit} disabled={!text.trim()}>
                Done
              </button>
            </div>
          </div>
        </>
      ) : (
        <div className="diff-after">{s.after}</div>
      )}
      {!editing && s.reason && <div className="small text-muted mt-2">{s.reason}</div>}
      {!editing && s.suggested !== undefined && (
        <button type="button" className="btn btn-link btn-sm p-0 mt-1 text-decoration-none" onClick={revert}>
          <i className="bi bi-arrow-counterclockwise me-1" />
          Use the original suggestion
        </button>
      )}
      {notFound && s.accepted && (
        <div className="small text-warning-emphasis mt-2">
          <i className="bi bi-exclamation-triangle me-1" />
          This line wasn't found in your Word file, so it was left as is.
        </div>
      )}
    </div>
  );
}

function ResumeTab({ job, onNext }) {
  const { documents, updateJob, completeStep, toast, integrations } = useApp();
  const defaultResume = documents.resumes.find((r) => r.isDefault) || documents.resumes[0];
  const [resumeId, setResumeId] = useState(defaultResume?.id || "");
  const [loading, setLoading] = useState(false);
  const [draft, setDraft] = useState(null);
  const [exporting, setExporting] = useState("");
  const [notFound, setNotFound] = useState([]);

  const result = draft || job.tailoredResume;
  const isSaved = !draft && !!job.tailoredResume;

  if (documents.resumes.length === 0) {
    return (
      <div className="card">
        <EmptyState
          icon="bi-file-earmark-arrow-up"
          title="Upload your resume first"
          text="We'll use it as the starting point and tailor it to this job."
          action={
            <Link to="/documents" className="btn btn-primary btn-sm">
              Upload resume
            </Link>
          }
        />
      </div>
    );
  }

  const run = async () => {
    setLoading(true);
    try {
      const { result: tailored } = await assistantApi.tailorResume(job.id, resumeId);
      setDraft({ ...tailored, baseResumeId: resumeId });
      setNotFound([]);
    } catch (err) {
      toast(err.message, "danger");
    } finally {
      setLoading(false);
    }
  };

  // Editing a saved result turns it back into a draft until it's saved again.
  const changeSuggestion = (sid, changes) =>
    setDraft((d) => {
      const base = d || job.tailoredResume;
      return { ...base, suggestions: base.suggestions.map((s) => (s.id === sid ? { ...s, ...changes } : s)) };
    });

  const save = async () => {
    const tailoredResume = { ...draft, baseResumeId: draft.baseResumeId || resumeId, savedAt: new Date().toISOString() };
    const saved = job.steps?.resume
      ? await updateJob(job.id, { tailoredResume })
      : await completeStep(job.id, "resume", "Tailored resume", { tailoredResume });
    if (!saved) return;
    setDraft(null);
    toast(job.tailoredResume ? "Changes saved" : "Tailored resume saved");
  };

  const baseResume = result && documents.resumes.find((r) => r.id === (result.baseResumeId || resumeId));
  const canExport = !!baseResume?.isWord;

  const exportFile = async (format) => {
    setExporting(format);
    try {
      const res = await jobsApi.resumeFile(job.id, { format, suggestions: result.suggestions, baseResumeId: baseResume.id });
      const { pages, unmatched } = await downloadResponse(res, `Resume.${format}`);
      setNotFound(unmatched);
      if (pages > 1) {
        toast(`Your resume now runs to ${pages} pages. Skip or shorten an edit to get it back on one page.`, "warning");
      } else if (unmatched.length) {
        toast(`${unmatched.length} edit${unmatched.length > 1 ? "s" : ""} couldn't be placed in your Word file. See the notes below.`, "warning");
      }
    } catch (err) {
      toast(err.message, "danger");
    } finally {
      setExporting("");
    }
  };

  // Older saves have no base text, so fall back to just the edited lines.
  const tailoredText = () => {
    if (!result.baseText) {
      return result.suggestions
        .filter((s) => s.accepted)
        .map((s) => `[${s.section}]\n${s.after}`)
        .join("\n\n");
    }
    return applyEdits(result.baseText, result.suggestions).text;
  };
  const copy = async () => toast((await copyText(tailoredText())) ? "Tailored resume copied" : "Couldn't copy", "info");

  const selectedResume = documents.resumes.find((r) => r.id === resumeId);
  const added = result?.added ?? result?.missing ?? [];
  const keptCount = result?.suggestions.filter((s) => s.accepted).length ?? 0;

  return (
    <div className="card">
      <div className="card-body">
        <h2 className="h6 mb-1">Tailor your resume</h2>
        <p className="text-muted small mb-3">
          We compare your resume with this job and suggest rewrites that use its language. We only rephrase what's already
          true; nothing gets made up.
        </p>

        {!result && (
          <div className="row g-2 align-items-end">
            <div className="col-sm-8">
              <label className="form-label small fw-medium" htmlFor="base-resume">
                Start from
              </label>
              <select id="base-resume" className="form-select" value={resumeId} onChange={(e) => setResumeId(e.target.value)}>
                {documents.resumes.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.name}
                    {r.isDefault ? " (default)" : ""}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-sm-4 d-grid">
              <button className="btn btn-primary" onClick={run} disabled={loading || !integrations.ai}>
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" /> Tailoring…
                  </>
                ) : (
                  <>
                    <i className="bi bi-stars me-1" /> Tailor resume
                  </>
                )}
              </button>
              <UsageLeft className="form-text text-center mt-1" />
            </div>
            {!integrations.ai && (
              <div className="col-12">
                <AccessNotice feature="Resume tailoring" />
              </div>
            )}
            {integrations.ai && selectedResume?.hasFile && !selectedResume.hasText && (
              <div className="col-12">
                <div className="alert alert-warning py-2 small mb-0">
                  We couldn't read any text from this file (scanned PDFs and old .doc files don't work). Upload a PDF or
                  DOCX exported from your editor.
                </div>
              </div>
            )}
            {selectedResume && !selectedResume.isWord && (
              <div className="col-12">
                <div className="hint-box">
                  <i className="bi bi-file-earmark-word" />
                  <span>
                    For a tailored PDF in your exact format, start from the Word (.docx) version of your resume.{" "}
                    <Link to="/documents">Upload it in Documents</Link>.
                  </span>
                </div>
              </div>
            )}
          </div>
        )}

        {loading && !result && (
          <div className="text-center text-muted small py-4">
            Comparing your resume with the job. This takes about 10–20 seconds.
          </div>
        )}

        {result && (
          <>
            {isSaved && (
              <div className="alert alert-success py-2 small d-flex align-items-center gap-2">
                <i className="bi bi-check-circle-fill" /> Tailored resume saved for {job.company}.
              </div>
            )}
            <div className="row g-4 mb-3">
              <div className="col-md-6">
                <ScoreBar label="Match before" value={result.scoreBefore} color="#94a3b8" />
                <ScoreBar label="Match after" value={result.scoreAfter} color="#16a34a" />
              </div>
              <div className="col-md-6 small">
                <div className="text-muted mb-1">Keywords you already cover</div>
                <div className="d-flex flex-wrap gap-1 mb-2">
                  {result.matched.map((k) => (
                    <span key={k} className="tag tag-success">
                      <i className="bi bi-check" /> {k}
                    </span>
                  ))}
                </div>
                {added.length > 0 && (
                  <>
                    <div className="text-muted mb-1">Brought out by these edits</div>
                    <div className="d-flex flex-wrap gap-1 mb-2">
                      {added.map((k) => (
                        <span key={k} className="tag tag-warning">
                          <i className="bi bi-plus" /> {k}
                        </span>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>

            {result.gaps?.length > 0 && (
              <div className="hint-box mb-3">
                <i className="bi bi-info-circle" />
                <span>
                  <strong>Not in your resume:</strong> {result.gaps.join(", ")}. We didn't add these. If you do have this
                  experience, add it yourself.
                </span>
              </div>
            )}

            <div className="d-flex justify-content-between align-items-baseline mb-2">
              <h3 className="h6 small text-uppercase text-muted fw-semibold mb-0">Suggested edits</h3>
              <span className="small text-muted">
                Keeping {keptCount} of {result.suggestions.length}
              </span>
            </div>
            <div className="d-flex flex-column gap-2 mb-3">
              {result.suggestions.map((s) => (
                <SuggestionCard
                  key={s.id}
                  suggestion={s}
                  notFound={notFound.includes(s.id)}
                  onChange={(changes) => changeSuggestion(s.id, changes)}
                />
              ))}
            </div>

            {!canExport && (
              <div className="hint-box mb-3">
                <i className="bi bi-file-earmark-word" />
                <span>
                  To download this as a PDF in your resume's exact format, upload the Word (.docx) version in{" "}
                  <Link to="/documents">Documents</Link>, make it your default, and tailor again.
                </span>
              </div>
            )}

            <div className="d-flex flex-wrap gap-2">
              {canExport && (
                <>
                  <button className="btn btn-outline-primary" onClick={() => exportFile("pdf")} disabled={!!exporting}>
                    {exporting === "pdf" ? (
                      <span className="spinner-border spinner-border-sm me-1" />
                    ) : (
                      <i className="bi bi-file-earmark-pdf me-1" />
                    )}
                    {exporting === "pdf" ? "Making PDF…" : "Download PDF"}
                  </button>
                  <button className="btn btn-light" onClick={() => exportFile("docx")} disabled={!!exporting} title="Download as a Word file">
                    {exporting === "docx" ? <span className="spinner-border spinner-border-sm me-1" /> : <i className="bi bi-file-earmark-word me-1" />}
                    Word
                  </button>
                </>
              )}
              <button className="btn btn-light" onClick={copy}>
                <i className="bi bi-clipboard me-1" /> Copy text
              </button>
              {isSaved ? (
                <>
                  <button className="btn btn-light" onClick={run} disabled={loading}>
                    {loading ? <span className="spinner-border spinner-border-sm me-1" /> : <i className="bi bi-arrow-repeat me-1" />}
                    Redo
                  </button>
                  <button className="btn btn-primary ms-sm-auto" onClick={onNext}>
                    Next: cover letter <i className="bi bi-arrow-right" />
                  </button>
                </>
              ) : (
                <>
                  <button className="btn btn-light ms-sm-auto" onClick={() => setDraft(null)}>
                    {job.tailoredResume ? "Undo changes" : "Discard"}
                  </button>
                  <button className="btn btn-primary" onClick={save}>
                    <i className="bi bi-check-lg me-1" /> {job.tailoredResume ? "Save changes" : "Save tailored resume"}
                  </button>
                </>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default ResumeTab;
